import * as React from 'react';
import { useCallback } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDown,
  MapPinnedIcon,
  Plus,
  Trash2,
} from 'lucide-react';

import { Button } from 'components/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { StopForm } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/Stop';
import {
  MAX_MCLEOD_ROUTE_STOPS,
  createMcleodIntermediateStop,
  mcleodRouteStopCollapsedSummary,
  mcleodRouteStopLabel,
  migrateMcleodLoadLevelPoNumsToRouteStops,
  normalizeMcleodRouteStopTypes,
  restoreMcleodLoadLevelPoNumsFromRouteStops,
  syncMcleodRouteStopsToPickupConsignee,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/mcleodRouteStops';
import { MultiStopBetaTooltip } from 'pages/QuoteView/LoadBuilding/components/MultiStopBetaTooltip';
import { NormalizedLoad, TMSLocation } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

type McleodRouteSectionProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  activeTabs: string[];
  activeStopTab?: string;
  activeStopTabFocusKey?: number;
  onActiveStopTabHandled?: () => void;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  pickupAiHighlightedExternalTMSID?: string;
  consigneeAiHighlightedExternalTMSID?: string;
  showStopComments?: boolean;
};

enum RouteTabs {
  route = 'route',
}

export function McleodRouteSection({
  formMethods,
  activeTabs,
  activeStopTab,
  activeStopTabFocusKey = 0,
  onActiveStopTabHandled,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  setLocations,
  pickupAiHighlightedExternalTMSID,
  consigneeAiHighlightedExternalTMSID,
  showStopComments = false,
}: McleodRouteSectionProps) {
  const { control, setValue, getValues } = formMethods;
  const { fields, insert, remove, move } = useFieldArray({
    control,
    name: 'stops',
  });

  const stops = useWatch({ control, name: 'stops' }) ?? [];
  const [collapsedByFieldId, setCollapsedByFieldId] = React.useState<
    Record<string, boolean>
  >({});

  const isStopOpen = (fieldId: string) => collapsedByFieldId[fieldId] !== true;

  const setStopOpen = React.useCallback((fieldId: string, open: boolean) => {
    setCollapsedByFieldId((prev) => {
      if (open) {
        if (prev[fieldId] !== true) {
          return prev;
        }
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      }
      if (prev[fieldId] === true) {
        return prev;
      }
      return { ...prev, [fieldId]: true };
    });
  }, []);

  React.useEffect(() => {
    setCollapsedByFieldId((prev) => {
      const fieldIds = new Set(fields.map((field) => field.id));
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const [id, collapsed] of Object.entries(prev)) {
        if (fieldIds.has(id)) {
          next[id] = collapsed;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [fields]);

  React.useEffect(() => {
    if (!activeStopTab?.startsWith('stop-') || !activeStopTabFocusKey) {
      return;
    }

    const index = Number(activeStopTab.slice('stop-'.length));
    if (!Number.isFinite(index) || index < 0 || index >= fields.length) {
      onActiveStopTabHandled?.();
      return;
    }

    const fieldId = fields[index]?.id;
    if (fieldId) {
      setStopOpen(fieldId, true);
    }
    onActiveStopTabHandled?.();
  }, [
    activeStopTab,
    activeStopTabFocusKey,
    fields,
    onActiveStopTabHandled,
    setStopOpen,
  ]);

  // Normalize stop types and, when the route is already multi-stop with a lingering
  // load-level PO (form restore / builtLoad), seed empty stop POs then clear poNums —
  // same as the 2→3 expand path.
  const syncNormalizedStops = useCallback(() => {
    const { stops: nextStops, poNums: nextPoNums } =
      migrateMcleodLoadLevelPoNumsToRouteStops(
        normalizeMcleodRouteStopTypes(getValues('stops') ?? []),
        getValues('poNums')
      );
    setValue('stops', nextStops, { shouldDirty: true });
    setValue('poNums', nextPoNums, { shouldDirty: true });
  }, [getValues, setValue]);

  const handleAddStop = useCallback(() => {
    if (fields.length >= MAX_MCLEOD_ROUTE_STOPS) {
      return;
    }

    const insertIndex = fields.length - 1;
    insert(insertIndex, createMcleodIntermediateStop(insertIndex));
    syncNormalizedStops();
  }, [fields.length, insert, syncNormalizedStops]);

  const handleRemoveStop = useCallback(
    (index: number) => {
      if (fields.length <= 2 || index <= 0 || index >= fields.length - 1) {
        return;
      }

      const willCollapseToTwoStops = fields.length - 1 === 2;
      remove(index);

      if (willCollapseToTwoStops) {
        const { stops: restoredStops, poNums } =
          restoreMcleodLoadLevelPoNumsFromRouteStops(
            normalizeMcleodRouteStopTypes(getValues('stops') ?? []),
            getValues('poNums')
          );
        const synced = syncMcleodRouteStopsToPickupConsignee(restoredStops);
        setValue('stops', restoredStops, { shouldDirty: true });
        setValue('poNums', poNums, { shouldDirty: true });
        setValue('pickup', synced.pickup, { shouldDirty: true });
        setValue('consignee', synced.consignee, { shouldDirty: true });
        return;
      }

      syncNormalizedStops();
    },
    [fields.length, getValues, remove, setValue, syncNormalizedStops]
  );

  const handleMoveStop = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (
        toIndex < 1 ||
        toIndex >= fields.length - 1 ||
        fromIndex < 1 ||
        fromIndex >= fields.length - 1
      ) {
        return;
      }

      move(fromIndex, toIndex);
      syncNormalizedStops();
    },
    [fields.length, move, syncNormalizedStops]
  );

  return (
    <LoadSectionAccordionItem
      label='Route'
      icon={<MapPinnedIcon className='h-6 w-6' strokeWidth={1} />}
      name={RouteTabs.route}
      activeTabs={activeTabs}
    >
      <Flex direction='col' gap='md' className='w-full'>
        {fields.map((field, index) => {
          const routeStopList =
            stops.length === fields.length ? stops : (getValues('stops') ?? []);
          const title = mcleodRouteStopLabel(routeStopList, index);
          const summary = mcleodRouteStopCollapsedSummary(routeStopList[index]);
          const isOpen = isStopOpen(field.id);
          const isFirstStop = index === 0;
          const isLastStop = index === fields.length - 1;
          const isIntermediate = !isFirstStop && !isLastStop;
          const moveUpDisabled = !isIntermediate || index <= 1;
          const moveDownDisabled =
            !isIntermediate || index >= fields.length - 2;
          const removeDisabled = !isIntermediate || fields.length <= 2;

          let aiHighlightedExternalTMSID = undefined;
          if (isFirstStop) {
            aiHighlightedExternalTMSID = pickupAiHighlightedExternalTMSID;
          } else if (isLastStop) {
            aiHighlightedExternalTMSID = consigneeAiHighlightedExternalTMSID;
          }

          return (
            <Collapsible
              key={field.id}
              open={isOpen}
              onOpenChange={(open) => setStopOpen(field.id, open)}
              className='w-full min-w-0 rounded-md border border-neutral-200 p-3'
            >
              <Flex
                align='start'
                justify='between'
                className='w-full'
                gap='sm'
                wrap='none'
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type='button'
                    variant='unstyled'
                    size='auto'
                    className='flex min-w-0 w-full flex-1 items-start justify-start gap-2 rounded-sm px-0 py-0 text-left font-normal hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300'
                    buttonNamePosthog={null}
                    aria-label={
                      isOpen ? `Collapse ${title}` : `Expand ${title}`
                    }
                  >
                    <ChevronDown
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200',
                        !isOpen && '-rotate-90'
                      )}
                    />
                    <Flex direction='col' gap='none' className='min-w-0 flex-1'>
                      <Typography
                        variant='body-sm'
                        weight='semibold'
                        className='min-w-0 truncate'
                      >
                        {title}
                      </Typography>
                      {!isOpen && summary ? (
                        <Typography
                          variant='body-xs'
                          className='truncate text-neutral-500'
                        >
                          {summary}
                        </Typography>
                      ) : null}
                    </Flex>
                  </Button>
                </CollapsibleTrigger>
                {isIntermediate ? (
                  <Flex
                    align='center'
                    gap='xs'
                    wrap='none'
                    className='shrink-0'
                  >
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 px-2'
                      buttonNamePosthog={null}
                      title={moveUpDisabled ? 'Cannot move pickup' : 'Move up'}
                      disabled={moveUpDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveStop(index, index - 1);
                      }}
                    >
                      <ArrowUpIcon className='h-4 w-4' />
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 px-2'
                      buttonNamePosthog={null}
                      title={
                        moveDownDisabled ? 'Cannot move consignee' : 'Move down'
                      }
                      disabled={moveDownDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveStop(index, index + 1);
                      }}
                    >
                      <ArrowDownIcon className='h-4 w-4' />
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 w-8 shrink-0 px-0 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 disabled:text-neutral-300'
                      buttonNamePosthog={null}
                      title={
                        removeDisabled
                          ? 'At least one pickup and one consignee are required'
                          : 'Remove stop'
                      }
                      disabled={removeDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemoveStop(index);
                      }}
                    >
                      <Trash2 className='h-4 w-4' />
                    </Button>
                  </Flex>
                ) : null}
              </Flex>

              <CollapsibleContent
                forceMount
                hidden={!isOpen}
                className='min-w-0 w-full pt-3'
              >
                <StopForm
                  stopKey={`stops.${index}`}
                  formMethods={formMethods}
                  isLoadingLocations={isLoadingLocations}
                  locations={locations}
                  handleRefreshLocations={handleRefreshLocations}
                  setLocations={setLocations}
                  aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
                  showPoField
                  showComments={showStopComments}
                />
              </CollapsibleContent>
            </Collapsible>
          );
        })}

        {fields.length < MAX_MCLEOD_ROUTE_STOPS && (
          <Flex justify='center' className='w-full mt-1'>
            <MultiStopBetaTooltip>
              <Button
                buttonNamePosthog={ButtonNamePosthog.QuickQuoteAddStop}
                type='button'
                variant='ghost'
                onClick={handleAddStop}
                className='w-fit text-xs text-neutral-800 pl-1.5 hover:border-neutral-700 hover:bg-neutral-100 h-8 px-2.5'
                size='sm'
              >
                <Plus className='h-4 w-4 mr-1' />
                Add Stop
              </Button>
            </MultiStopBetaTooltip>
          </Flex>
        )}
      </Flex>
    </LoadSectionAccordionItem>
  );
}

export { RouteTabs as McleodRouteTabs };
